import { useEffect, useRef } from 'react';

/*
 * Root-growth visual — fractal branches grow outward from a base point like
 * roots spreading underground, with glowing tips and a slow "sap flow" pulse
 * travelling along the branches once fully grown. Distinct from a generic
 * neural-network dot field, and ties directly to the AerialRoot brand.
 */
export default function RootCanvas({ className = '' }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let W, H, branches = [], pulses = [];
    let growthStart = performance.now();
    const GROWTH_MS = 3600;

    const rand = (a, b) => Math.random() * (b - a) + a;

    function buildBranches() {
      branches = [];
      const baseX = W * 0.72;
      const baseY = H * 1.02;

      function grow(x, y, angle, len, depth, order) {
        if (depth > 6 || len < 8) return;
        const wobble = rand(-0.35, 0.35);
        const a2 = angle + wobble;
        const ex = x + Math.cos(a2) * len;
        const ey = y + Math.sin(a2) * len;
        branches.push({
          x1: x, y1: y, x2: ex, y2: ey,
          depth, order,
          delay: depth * 0.13 + order * 0.02,
          width: Math.max(3.2 - depth * 0.45, 0.6),
        });
        const kids = depth < 2 ? 3 : (Math.random() > 0.4 ? 2 : 1);
        for (let i = 0; i < kids; i++) {
          const spread = (i - (kids - 1) / 2) * rand(0.35, 0.65);
          grow(ex, ey, a2 + spread, len * rand(0.68, 0.8), depth + 1, i);
        }
      }

      // Two main root trunks growing up-left from the base corner
      grow(baseX, baseY, -Math.PI / 2 - 0.5, H * 0.24, 0, 0);
      grow(baseX, baseY, -Math.PI / 2 + 0.15, H * 0.22, 0, 1);

      growthStart = performance.now();
      pulses = branches
        .filter(b => b.depth <= 2)
        .map(b => ({ branch: b, t: Math.random(), speed: rand(0.15, 0.3) }));
    }

    const resize = () => {
      W = canvas.width = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
      buildBranches();
    };

    function drawBranch(b, growthT) {
      const t = Math.max(0, Math.min(1, (growthT - b.delay) / 0.32));
      if (t <= 0) return t;
      const ex = b.x1 + (b.x2 - b.x1) * t;
      const ey = b.y1 + (b.y2 - b.y1) * t;
      const alpha = 0.55 - b.depth * 0.055;
      const grad = ctx.createLinearGradient(b.x1, b.y1, ex, ey);
      grad.addColorStop(0, `rgba(61,140,40,${alpha})`);
      grad.addColorStop(1, `rgba(141,214,116,${alpha})`);
      ctx.beginPath();
      ctx.moveTo(b.x1, b.y1);
      ctx.lineTo(ex, ey);
      ctx.strokeStyle = grad;
      ctx.lineWidth = b.width;
      ctx.lineCap = 'round';
      ctx.stroke();

      // glowing growth tip while actively extending
      if (t > 0 && t < 1) {
        ctx.beginPath();
        ctx.arc(ex, ey, b.width * 1.4, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(141,214,116,0.9)';
        ctx.shadowBlur = 10; ctx.shadowColor = '#8dd674';
        ctx.fill(); ctx.shadowBlur = 0;
      }
      return t;
    }

    let raf;
    function animate(now) {
      ctx.clearRect(0, 0, W, H);
      const growthT = (now - growthStart) / GROWTH_MS;

      branches.forEach(b => drawBranch(b, growthT));

      // sap-flow pulses travelling along fully-grown near-trunk branches
      if (growthT > 1) {
        pulses.forEach(p => {
          p.t += p.speed * 0.012;
          if (p.t > 1) p.t = 0;
          const b = p.branch;
          const px = b.x1 + (b.x2 - b.x1) * p.t;
          const py = b.y1 + (b.y2 - b.y1) * p.t;
          ctx.beginPath();
          ctx.arc(px, py, 2.6, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(200,232,188,0.85)';
          ctx.shadowBlur = 8; ctx.shadowColor = '#c8e8bc';
          ctx.fill(); ctx.shadowBlur = 0;
        });
      }

      raf = requestAnimationFrame(animate);
    }

    resize();
    window.addEventListener('resize', resize);
    raf = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        opacity: 0.8,
        pointerEvents: 'none',
      }}
    />
  );
}
